import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DEEPLINK_PARAMS } from '../../../../shared/constants/deeplinkParams';
import type { LinkingMethod } from '../../useLinkingWorkflow';

// Sincroniza ?linking_step / ?linking_method con el step de credenciales.
// `syncUrl=false` (superficies embebidas como consult-RFC) lo desactiva: solo
// limpia params entrantes en mount, nunca los escribe.
export const useLinkingUrlSync = ({
	step,
	linkingOptionSelected,
	syncUrl,
}: {
	step: number;
	linkingOptionSelected: LinkingMethod;
	syncUrl: boolean;
}) => {
	const [, setSearchParams] = useSearchParams();

	// Strip transient deep-link params on mount so they don't survive a refresh
	// while the linking screen is open.
	useEffect(() => {
		setSearchParams(
			(prev) => {
				const flow = prev.get('linking_flow');
				if (
					!prev.get(DEEPLINK_PARAMS.LINKING_STEP) &&
					!prev.get(DEEPLINK_PARAMS.LINKING_METHOD) &&
					!flow
				) {
					return prev;
				}
				const next = new URLSearchParams(prev);
				next.delete(DEEPLINK_PARAMS.LINKING_STEP);
				next.delete(DEEPLINK_PARAMS.LINKING_METHOD);
				if (flow && ['efirma', 'link'].includes(flow)) {
					next.delete('linking_flow');
				}
				return next;
			},
			{ replace: true },
		);
		// only on mount
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Keep the URL in sync with the credentials step so refresh + share links
	// land in the right place.
	useEffect(() => {
		if (!syncUrl) return;
		setSearchParams(
			(prev) => {
				const currentStep = prev.get(DEEPLINK_PARAMS.LINKING_STEP);
				const currentMethod = prev.get(DEEPLINK_PARAMS.LINKING_METHOD);
				if (step !== 2 || !linkingOptionSelected) {
					if (!currentStep && !currentMethod) return prev;
					const next = new URLSearchParams(prev);
					next.delete(DEEPLINK_PARAMS.LINKING_STEP);
					next.delete(DEEPLINK_PARAMS.LINKING_METHOD);
					return next;
				}
				const method = linkingOptionSelected === 'efirma' ? 'efirma' : 'ciec';
				if (currentStep === 'credentials' && currentMethod === method)
					return prev;
				const next = new URLSearchParams(prev);
				next.set(DEEPLINK_PARAMS.LINKING_STEP, 'credentials');
				next.set(DEEPLINK_PARAMS.LINKING_METHOD, method);
				return next;
			},
			{ replace: true },
		);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [step, linkingOptionSelected, syncUrl]);
};
